/**
 * KB typed API (Phase 7). Every shape — input body, path params, resolved
 * value — derives from the SERVER's route defs via the type-only import below
 * (erased at build; no server code reaches the bundle). A server-side shape
 * change breaks this file and its callers at compile time.
 *
 * The createWorkspace/query/reembed defs carry Zod input schemas, so those
 * callers take fully typed inputs. The documents list paginates via in-handler
 * parsing (no query schema) — callers pass page/pageSize through typedRoute's
 * free-form `query` field.
 *
 * Deliberately NOT here:
 * - POST /workspaces/:id/documents (uploadDocument): multipart/form-data, and
 *   the handler returns raw Responses (202 enqueue / 200 dedup) — the upload
 *   hook hand-rolls that fetch (the typed client is JSON-only).
 */
import { typedRoute } from "@d12labs/client";
import type { kbRouteDefs } from "../server/routes";

export const kbApi = {
	listWorkspaces: typedRoute<typeof kbRouteDefs.listWorkspaces>()(
		"GET",
		"/api/plugins/kb/workspaces",
	),
	createWorkspace: typedRoute<typeof kbRouteDefs.createWorkspace>()(
		"POST",
		"/api/plugins/kb/workspaces",
	),
	query: typedRoute<typeof kbRouteDefs.query>()("POST", "/api/plugins/kb/query"),
	reembed: typedRoute<typeof kbRouteDefs.reembed>()("POST", "/api/plugins/kb/reembed"),
	workspace: typedRoute<typeof kbRouteDefs.workspace>()("GET", "/api/plugins/kb/workspaces/:id"),
	removeWorkspace: typedRoute<typeof kbRouteDefs.removeWorkspace>()(
		"DELETE",
		"/api/plugins/kb/workspaces/:id",
	),
	workspaceStats: typedRoute<typeof kbRouteDefs.workspaceStats>()(
		"GET",
		"/api/plugins/kb/workspaces/:id/stats",
	),
	listDocuments: typedRoute<typeof kbRouteDefs.listDocuments>()(
		"GET",
		"/api/plugins/kb/workspaces/:id/documents",
	),
	document: typedRoute<typeof kbRouteDefs.document>()("GET", "/api/plugins/kb/documents/:id"),
	removeDocument: typedRoute<typeof kbRouteDefs.removeDocument>()(
		"DELETE",
		"/api/plugins/kb/documents/:id",
	),
	documentPreview: typedRoute<typeof kbRouteDefs.documentPreview>()(
		"GET",
		"/api/plugins/kb/documents/:id/preview",
	),
};

/** The server's response shapes, derived — never redeclared by hand. */

/** A KB workspace row (item of `GET /workspaces`). */
export type KbWorkspace = Awaited<ReturnType<typeof kbApi.listWorkspaces>>[number];

/** The `paginated(...)` page returned by `GET /workspaces/:id/documents`. */
export type DocumentPage = Awaited<ReturnType<typeof kbApi.listDocuments>>;

/** A KB document row (item of the documents page). */
export type KbDocument = DocumentPage["items"][number];

/** Aggregate document stats for a workspace (`GET /workspaces/:id/stats`). */
export type KbWorkspaceStats = Awaited<ReturnType<typeof kbApi.workspaceStats>>;

/** Response of `POST /query`. `answer` is null when retrieval found no grounding. */
export type QueryResult = Awaited<ReturnType<typeof kbApi.query>>;

/** One cited source in a query answer (projected from the retrieved chunks). */
export type QuerySource = QueryResult["sources"][number];

/** The structured grounded answer the synthesis returns (`POST /query`). */
export type KbStructuredAnswer = NonNullable<QueryResult["answer"]>;

/** Response of `GET /documents/:id/preview`. */
export type KbDocumentPreview = Awaited<ReturnType<typeof kbApi.documentPreview>>;
